<template>
    <div class="results">
        <h1 class="mb-4 mt-5 border-bottom border-secondary border-3 pb-1">
            <span>{{ client.name }}:</span> <em>{{ plan.name }}</em>
            <button class="btn btn-primary float-end btn-sm d-none d-sm-inline-block" @click="customize = !customize">
                {{ customize ? 'Finished' : 'Customize Dashboard' }}
            </button>
        </h1>

        <!-- Notifications -->
        <Transition name="fade-slow">
            <div v-if="notifs.length > 0" class="notifications-container">
                <div class="alert mb-4 box-content" v-for="n in notifs" :key="n.id" :class="n.data.class">
                    <button class="btn btn-white px-0 close-btn" @click="dismissNotification(n)">
                        <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                    </button>
                    {{ n.data.message }}
                </div>
            </div>
        </Transition>

        <!-- Overview Scorecards -->
        <div class="row stat-container" :class="{ customize }">
            <div class="col-sm-6 col-md-4 mb-4 scorecard" 
                v-for="m in metrics" 
                :key="m"
                :class="{ 'visually-hidden': !isVisible(m) }">
                <button type="button" 
                    class="close btn" 
                    aria-label="Close"
                    @click="customizeVisibility(m)">
                    <i class="fa fa-times" aria-hidden="true"></i>
                </button>
                <div class="card" @click="current_metric = m">
                    <h4 class="card-header pt-3">{{ propercase(m) }}</h4>
                    <div class="card-body pt-0 pb-3">
                        <img :src="getMetricIcon(m)" class="float-start me-3">
                        <h3 class="mt-2">
                            {{ formatNumber(m, annual_stats[m]['actuals']) }}
                            <span class="badge" 
                                :class="{ 
                                    'bg-green bg-icon-increase': annual_stats[m]['variance'] > 0, 
                                    'bg-red bg-icon-decrease': annual_stats[m]['variance'] < 0
                                }">
                                {{ percentage_na(annual_stats[m]['variance']) }}
                            </span>
                        </h3>
                    </div>
                </div>
            </div>
        </div>

        <!-- Chart -->
        <div class="card mb-5" v-if="numMetricsVisible() > 0">
            <div class="card-body">
                <div class="row">
                    <div class="col-4">
                        <h3>{{ page_view === 'ytd' ? "Year to Date" : "Total" }} {{ propercase(current_metric) }}</h3>
                    </div>
                    <div class="col-8">
                        <div class="btn-toolbar flex-row-reverse" role="toolbar" aria-label="Toolbar with button groups">
                            <div class="btn-group mx-2 mb-2 result-selector btn-group-sm" role="group">
                                <button type="button" 
                                    class="btn"
                                    v-for="m in metrics" 
                                    :key="m"
                                    @click="current_metric = m"
                                    v-if="isVisible(m)"
                                    :class="current_metric === m ? 'btn-primary' : 'btn-secondary'">
                                    {{ propercase(m) }}
                                </button>
                            </div>

                            <div class="btn-group mx-2 mb-2 btn-group-sm" role="group">
                                <button type="button" 
                                    class="btn" 
                                    @click="current_view = 'monthly'"
                                    :class="current_view === 'monthly' ? 'btn-primary' : 'btn-secondary'">
                                    Monthly
                                </button>
                                <button type="button" 
                                    class="btn" 
                                    @click="current_view = 'cumulative'"
                                    :class="current_view === 'cumulative' ? 'btn-primary' : 'btn-secondary'">
                                    Cumulative
                                </button>
                            </div>

                            <div class="btn-group mx-2 mb-2 btn-group-sm" role="group">
                                <button type="button" 
                                    class="btn" 
                                    @click="page_view = 'ytd'"
                                    :class="page_view === 'ytd' ? 'btn-primary' : 'btn-secondary'">
                                    Year to Date
                                </button>
                                <button type="button" 
                                    class="btn" 
                                    @click="page_view = 'full'"
                                    :class="page_view === 'full' ? 'btn-primary' : 'btn-secondary'">
                                    Full Year
                                </button>
                                <button type="button" 
                                    class="btn btn-secondary" 
                                    @click="changeYear(-1)">
                                    Last Year
                                </button>
                                <button type="button" 
                                    class="btn btn-secondary" 
                                    @click="changeYear(1)">
                                    Next Year
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row chart-container">
                    <div class="col">
                        <canvas id="stats-chart"></canvas>
                    </div>
                </div>
            </div>
        </div>

        <TransitionGroup name="move" tag="div">
            <div class="row results-container align-items-center" 
                v-for="(init_ids, index) in sortedDataset()" 
                :key="init_ids"
                :class="{ customize }" 
                v-if="dataset[init_ids] !== null && (shouldRender(dataset[init_ids]) || customize)">
                <div class="col-1 text-center" v-if="customize">
                    <button class="btn btn-secondary my-2" 
                        v-if="index > 0"
                        @click="customizePosition(dataset[init_ids], -1)">
                        <i class="fas fa-chevron-up"></i>
                    </button>
                    <div class="clearfix"></div>
                    <button class="btn btn-secondary my-2" 
                        v-if="index < Object.keys(dataset).length-1"
                        @click="customizePosition(dataset[init_ids], 1)">
                        <i class="fas fa-chevron-down"></i>
                    </button>
                </div>
                <div class="col" v-if="hasAnyMetric(dataset[init_ids])">
                    <initiative-results-table 
                        :headers="headers" 
                        :initiative="dataset[init_ids]"
                        :client="client" 
                        @addCustomization="addCustomization"
                        @deleteCustomization="deleteCustomization" 
                        :customizations="local_custs"
                        :start_date="start_date" 
                        :end_date="end_date">
                    </initiative-results-table>
                </div>
                <div class="col" v-else>
                    <h3>{{ dataset[init_ids].title }}</h3>
                    <div class="card mb-5">
                        <div class="card-body">
                            <p class="mb-0">The initiative has not launched yet so there are no metrics to display.</p>
                        </div>
                    </div>
                </div>
            </div>
        </TransitionGroup>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import Swal from 'sweetalert2';
import moment from 'moment';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import InitiativeResultsTable from '../components/InitiativeResultsTable';

export default defineComponent({
    name: 'ProjVsActuals',
    components: { 
        Swal,
        InitiativeResultsTable
    },
    props: {
        plan: {
            type: Object,
            required: true
        },
        client: {
            type: Object,
            required: true
        },
        projections: {
            type: Array,
            required: true
        },
        actuals: {
            type: Array,
            required: true
        },
        customizations: {
            type: Array,
            required: true
        },
        notifications: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            chart: null,
            dataset: null,
            monthly_stats: null,
            page_view: 'ytd',
            current_view: 'monthly',
            current_metric: 'media_cost',
            metrics: ['media_cost', 'project_cost', 'new_donors', 'revenue', 'gifts', 'emails'],
            chart_data: [],
            initiative_types: [],
            start_date: null,
            end_date: new Date(),
            annual_stats: {
                emails: { projections: 0, actuals: 0, variance: 0 },
                media_cost: { projections: 0, actuals: 0, variance: 0 },
                project_cost: { projections: 0, actuals: 0, variance: 0 },
                revenue: { projections: 0, actuals: 0, variance: 0 },
                new_donors: { projections: 0, actuals: 0, variance: 0 },
                gifts: { projections: 0, actuals: 0, variance: 0 },
            },
            local_custs: null,
            customize: false,
            notifs: []
        };
    },
    computed: {
        headers() {
            const start_month = parseInt(this.plan.start_month.split("-")[1]);
            let len = 12;

            if(this.page_view === 'ytd') {
                const start_year = parseInt(this.plan.start_month.split("-")[0]);
                const dte = new Date();
                const end_month = dte.getMonth() + 1;
                const end_year = dte.getFullYear();

                len = (end_month - start_month + 1) + 12 * (end_year - start_year);
            }

            return Array.from({length: len}, (_, i) => {
                return new Date(0, i + start_month - 1).toLocaleString('en-US', {month: 'short'});
            });
        }
    },
    watch: {
        page_view(oldVal, newVal) {
            if(newVal === 'full')
                this.end_date = new Date();
            else
                this.end_date = this.getEndDate();
            this.populateChartData();
            this.dataset = this.getDataset();
        },
        current_metric() {
            this.populateChartData();
        },
        current_view() {
            this.populateChartData();
        }
    },
    mounted() {
        this.local_custs = this.customizations;
        this.notifs = this.notifications;

        //Find the first visible metrics
        for(let i = 0; i < this.metrics.length; i++) {
            if(this.isVisible(this.metrics[i])) {
                this.current_metric = this.metrics[i];
                break;
            }
        }

        this.start_date = this.getStartDate();
        this.dataset = this.getDataset();
        this.annual_stats = this.getAnnualStats();
        this.monthly_stats = this.getMonthlyStats();

        this.populateChartData();
        this.getInitiativeTypes();
    },
    methods: {
        changeYear(direction) {
            //Move the start date back a year
            this.page_view = 'full';
            this.start_date.setUTCFullYear(this.start_date.getUTCFullYear() + direction);
            this.dataset = this.getDataset();
            this.annual_stats = this.getAnnualStats();
            this.monthly_stats = this.getMonthlyStats();

            this.populateChartData();
        },
        dismissNotification(n) {
            window.axios.get('/api/notification/read/' + n.id);

            //Remove the notification from the list
            const index = this.notifs.findIndex(notification => notification.id === n.id);
            if(index !== -1) {
                this.notifs.splice(index, 1);
            }
        },
        shouldRender(init) {
            return (this.hasMetric(init, init.media_cost) && this.isInitMetricVisible(init, 'media_cost')) ||
                   (this.hasMetric(init, init.project_cost) && this.isInitMetricVisible(init, 'project_cost')) ||
                   (this.hasMetric(init, init.all_revenue) && this.isInitMetricVisible(init, 'all_revenue')) ||
                   (this.hasMetric(init, init.new_donors) && this.isInitMetricVisible(init, 'new_donors')) ||
                   (this.hasMetric(init, init.emails) && this.isInitMetricVisible(init, 'emails')) ||
                   (this.hasMetric(init, init.gifts) && this.isInitMetricVisible(init, 'gifts'));
        },
        hasAnyMetric(init) {
            return this.hasMetric(init, init.media_cost) ||
                   this.hasMetric(init, init.project_cost) ||
                   this.hasMetric(init, init.all_revenue) ||
                   this.hasMetric(init, init.new_donors) ||
                   this.hasMetric(init, init.emails) ||
                   this.hasMetric(init, init.gifts);
        },
        hasMetric(init, arr) {
            if(typeof arr === 'string')
                arr = init[arr];

            let total = 0;
            for(const [key, i] of Object.entries(arr.projections)) {
                total += i;
            }
            for(const [key, i] of Object.entries(arr.actuals)) {
                total += i;
            }
            return total !== 0;
        },
        isInitMetricVisible(init, metric) {
            if(this.local_custs === undefined || this.local_custs === null) return true;

            for(const [key, i] of Object.entries(this.local_custs)) {
                if(i.action === 'hide' && 
                   (i.filter.metric === metric || 
                    (i.filter.metric === 'all_revenue' && metric.indexOf('revenue') >= 0)) &&
                   i.filter.initiative_id === init.id) {
                    return false;
                }
            }
            return true;
        },
        numMetricsVisible() {
            return this.metrics.filter(metric => this.isVisible(metric)).length;
        },
        isVisible(metric) {
            if(this.local_custs === undefined) return true;

            for(const [key, i] of Object.entries(this.local_custs)) {
                if(i.action === 'hide' && 
                   i.filter.metric === metric && 
                   i.filter.initiative_id === null) {
                    return false;
                }
            }
            return true;
        },
        addCustomization(c) {
            this.local_custs.push(c);
        },
        deleteCustomization(index) {
            this.local_custs.splice(index, 1);
        },
        customizePosition(init, distance) {
            //Create the baseline data structure
            const data = {
                action: 'move',
                client_id: this.client.id,
                filter: {
                    initiative_id: init.id,
                    distance: distance
                }
            };

            //See if the customization already exists
            const existingIndex = this.local_custs.findIndex(c => 
                c.action === data.action && 
                c.filter.initiative_id === data.filter.initiative_id
            );

            if(existingIndex !== -1) {
                //Update the distance
                this.local_custs[existingIndex].filter.distance += distance;
                //Update the existing customization
                window.axios.put('/api/customization/' + this.local_custs[existingIndex].id, this.local_custs[existingIndex]);
            } else {
                //Create new customization
                window.axios.post('/api/customization', data)
                    .then(response => {
                        if(response.status === 200) {
                            this.addCustomization(response.data.data);
                        }
                    });
            }
        },
        customizeVisibility(metric) {
            const data = {
                action: 'hide',
                client_id: this.client.id,
                filter: {
                    metric: metric
                }
            };

            const existingIndex = this.local_custs.findIndex(c => 
                c.action === data.action && 
                c.filter.metric === data.filter.metric && 
                c.filter.initiative_id === null
            );

            if(existingIndex === -1) {
                window.axios.post('/api/customization', data)
                    .then(response => {
                        if(response.status === 200) {
                            this.addCustomization(response.data.data);
                        }
                    });
            } else {
                window.axios.delete('/api/customization/' + this.local_custs[existingIndex].id);
                this.deleteCustomization(existingIndex);

                //If all were previously hidden and now this one is showing, redraw the chart
                if(this.numMetricsVisible() === 1) {
                    this.current_metric = metric;
                    // Need to wait for the chart to render again before redrawing it
                    this.$nextTick(() => {
                        this.populateChartData();
                    });
                }
            }
        },
        formatNumber(metric, num) {
            switch(metric) {
                case 'media_cost':
                case 'project_cost':
                case 'revenue':
                    return this.currency_with_zero(num);
                default:
                    return this.number_with_zero(num);
            }
        },
        getMetricIcon(metric) {
            switch(metric) {
                case 'media_cost':
                case 'project_cost':
                    return '/img/icons/average_amount.png';
                case 'new_donors':
                    return "/img/icons/donors_new.png";
                case 'revenue':
                    return "/img/icons/revenue.png";
                case 'gifts':
                    return "/img/icons/donors.png";
                case 'emails':
                    return "/img/icons/subscribers.png";
                default:
                    return "";
            }
        },
        sortedDataset() {
            if(!this.dataset) return null;

            //Sort the initiatives based upon priority first
            const sortedKeys = Object.keys(this.dataset).sort(
                (a, b) => this.dataset[a].sort_priority - this.dataset[b].sort_priority
            );

            //Now move the array positions around based upon customizations
            for(const customization of this.local_custs) {
                if(customization.action === 'move') {
                    const index = sortedKeys.indexOf(customization.filter.initiative_id);
                    if(index !== -1) {
                        //Move the item
                        sortedKeys.splice(index, 1);
                        sortedKeys.splice(index + customization.filter.distance, 0, customization.filter.initiative_id);
                    }
                }
            }

            return sortedKeys;
        },
        getEmptyInitiative(i) {
            return {
                id: i.initiative_ids,
                title: i.title,
                priority: i.priority,
                sort_priority: i.sort_priority,
                url: i.url,
                cost: {
                    projections: {},
                    actuals: {},
                    variance: {}
                },
                project_cost: {
                    projections: {},
                    actuals: {},
                    variance: {}
                },
                media_cost: {
                    projections: {},
                    actuals: {},
                    variance: {}
                },
                new_donors: {
                    projections: {},
                    actuals: {},
                    variance: {}
                },
                gifts: {
                    projections: {},
                    actuals: {},
                    variance: {}
                },
                emails: {
                    projections: {},
                    actuals: {},
                    variance: {}
                },
                revenue: {
                    projections: {},
                    actuals: {},
                    variance: {}
                },
                recurring_revenue: {
                    projections: {},
                    actuals: {},
                    variance: {}
                },
                all_revenue: {
                    projections: {},
                    actuals: {},
                    variance: {}
                }
            };
        },
        addInitiativeValues(arr, arr_name, init) {
            arr.project_cost[arr_name][init.date] = parseFloat(init.project_cost);
            arr.media_cost[arr_name][init.date] = parseFloat(init.media_cost);
            arr.cost[arr_name][init.date] = parseFloat(init.media_cost) + parseFloat(init.project_cost);
            arr.new_donors[arr_name][init.date] = parseFloat(init.new_donors);
            arr.gifts[arr_name][init.date] = parseFloat(init.gifts);
            arr.emails[arr_name][init.date] = parseFloat(init.emails);
            arr.revenue[arr_name][init.date] = parseFloat(init.revenue);
            arr.recurring_revenue[arr_name][init.date] = parseFloat(init.recurring_revenue);
            arr.all_revenue[arr_name][init.date] = parseFloat(init.revenue) + parseFloat(init.recurring_revenue);
        },
        calculateVariance(arr, pos) {
            const proj = Object.keys(arr[pos].projections);
            const actu = Object.keys(arr[pos].actuals);
            const dates = this.getUniqueArray(proj.concat(actu));

            dates.forEach(dte => {
                if(arr[pos].projections[dte] === null || arr[pos].projections[dte] === 0)
                    arr[pos].variance[dte] = 0;
                else if(arr[pos].actuals[dte] === null || arr[pos].actuals[dte] === 0)
                    arr[pos].variance[dte] = -1;
                else
                    arr[pos].variance[dte] = (arr[pos].actuals[dte] - arr[pos].projections[dte]) / arr[pos].projections[dte];
            });
        },
        getMonthlyStats() {
            const dataset = this.getDataset();
            const data = {
                projections: [],
                actuals: []
            };

            //Loop through the start and end months
            for(let month = new Date(this.start_date); month < this.end_date; month.setUTCMonth(month.getUTCMonth() + 1)) {
                let actuals = 0;
                let projections = 0;

                //Now loop through each initiative
                for(const [key, el] of Object.entries(dataset)) {
                    //if it is revenue, combine onetime and recurring
                    if(this.current_metric === 'revenue') {
                        projections += el.revenue.projections[this.getDateAsString(month)] +
                            el.recurring_revenue.projections[this.getDateAsString(month)];
                        actuals += el.revenue.actuals[this.getDateAsString(month)] +
                            el.recurring_revenue.actuals[this.getDateAsString(month)];
                    }
                    //Otherwise, I can do a straight pull from the array
                    else {
                        projections += el[this.current_metric].projections[this.getDateAsString(month)];
                        actuals += el[this.current_metric].actuals[this.getDateAsString(month)];
                    }
                }

                data.projections.push(projections);
                data.actuals.push(actuals);
            }

            return data;
        },
        getDataset() {
            const inits = {};
            const blank_init = {
                id: -1,
                date: "",
                emails: 0,
                gifts: 0,
                lifetime_value: 0,
                media_cost: 0,
                new_donors: 0,
                project_cost: 0,
                recurring_revenue: 0,
                revenue: 0
            };

            //Loop through every projection to populate the array
            //Only include the data if it is between the start and end dates
            this.projections
                .filter(i => new Date(Date.parse(i.date)) <= this.end_date &&
                            new Date(Date.parse(i.date)) >= this.start_date)
                .forEach(i => {
                    if(inits[i.initiative_ids] === null)
                        inits[i.initiative_ids] = this.getEmptyInitiative(i);

                    this.addInitiativeValues(inits[i.initiative_ids], 'projections', i);

                    //In cases where there are no actuals, this will ensure there is at least on value
                    blank_init.date = i.date;
                    this.addInitiativeValues(inits[i.initiative_ids], 'actuals', blank_init);
                });

            //Loop through every "actual"
            this.actuals
                .filter(i => new Date(Date.parse(i.date)) <= this.end_date &&
                            new Date(Date.parse(i.date)) >= this.start_date)
                .forEach(i => {
                    if(inits[i.initiative_ids] === null)
                        inits[i.initiative_ids] = this.getEmptyInitiative(i);

                    this.addInitiativeValues(inits[i.initiative_ids], 'actuals', i);
                });

            //Now calculate the variance between projections and actuals
            for(const [key, i] of Object.entries(inits)) {
                this.calculateVariance(i, 'project_cost');
                this.calculateVariance(i, 'media_cost');
                this.calculateVariance(i, 'gifts');
                this.calculateVariance(i, 'new_donors');
                this.calculateVariance(i, 'emails');
                this.calculateVariance(i, 'revenue');
                this.calculateVariance(i, 'recurring_revenue');
            }

            return inits;
        },
        getAnnualStats() {
            const dataset = this.dataset;
            const data = this.annual_stats;

            //Loop through the start and end months
            for(let month = new Date(this.start_date); month < new Date(); month.setUTCMonth(month.getUTCMonth() + 1)) {
                //Now loop through each initiative
                for(const [key, el] of Object.entries(dataset)) {
                    //Combine project and media for costs
                    data.media_cost.projections += el.media_cost.projections[this.getDateAsString(month)];
                    data.media_cost.actuals += el.media_cost.actuals[this.getDateAsString(month)];

                    data.project_cost.projections += el.project_cost.projections[this.getDateAsString(month)];
                    data.project_cost.actuals += el.project_cost.actuals[this.getDateAsString(month)];

                    //For revenue
                    data.revenue.projections += el.all_revenue.projections[this.getDateAsString(month)];
                    data.revenue.actuals += el.all_revenue.actuals[this.getDateAsString(month)];

                    //Emails
                    data.emails.projections += el.emails.projections[this.getDateAsString(month)];
                    data.emails.actuals += el.emails.actuals[this.getDateAsString(month)];

                    //Gifts
                    data.gifts.projections += el.gifts.projections[this.getDateAsString(month)];
                    data.gifts.actuals += el.gifts.actuals[this.getDateAsString(month)];

                    //New Donors
                    data.new_donors.projections += el.new_donors.projections[this.getDateAsString(month)];
                    data.new_donors.actuals += el.new_donors.actuals[this.getDateAsString(month)];
                }
            }

            //Calculate the percent change
            for(const metric of this.metrics) {
                if(data[metric].projections > 0) {
                    data[metric].variance = (data[metric].actuals - data[metric].projections) / data[metric].projections;
                }
            }

            return data;
        },
        getStartDate() {
            let start_date = new Date(Date.parse(this.plan.start_month));
            const today = new Date();
            today.setFullYear(today.getUTCFullYear() - 1);
            while(start_date < today) {
                start_date.setUTCFullYear(start_date.getUTCFullYear() + 1);
            }
            return start_date;
        },
        getEndDate() {
            const end_date = new Date(this.start_date);
            end_date.setUTCFullYear(end_date.getUTCFullYear() + 1);
            end_date.setUTCDate(end_date.getUTCDate() - 1);
            return end_date;
        },
        getInitiativeTypes() {
            window.axios.get('/api/initiative_type')
                .then(response => {
                    this.initiative_types = response.data.data;
                });
        },
        populateChartData() {
            let type = 'bar';
            const monthly_stats = this.getMonthlyStats();

            //Make it cumulative
            if(this.current_view === 'cumulative') {
                for(let i = 1; i < monthly_stats.projections.length; i++) {
                    monthly_stats.projections[i] += monthly_stats.projections[i - 1];
                    monthly_stats.actuals[i] += monthly_stats.actuals[i - 1];
                }

                //Set up the data
                this.chart_data = [
                    {
                        label: 'Projections',
                        data: monthly_stats.projections,
                        backgroundColor: ['#C7CCDB'],
                        borderColor: ['#C7CCDB'],
                        borderDash: [10, 10],
                        borderWidth: 2,
                        tension: 0.1
                    },
                    {
                        label: 'Actuals',
                        data: monthly_stats.actuals,
                        backgroundColor: ['#46705F'],
                        borderColor: ['#46705F'],
                        borderWidth: 4,
                        tension: 0.1
                    }
                ];

                type = 'line';
            } else {
                this.chart_data = [
                    {
                        label: 'Projections',
                        data: monthly_stats.projections,
                        backgroundColor: ['rgb(199, 204, 219, 0.8)'],
                        borderColor: ['#C7CCDB'],
                        borderWidth: 1,
                        borderRadius: 6,
                        borderSkipped: false,
                        barPercentage: 0.8
                    },
                    {
                        label: 'Actuals',
                        data: monthly_stats.actuals,
                        backgroundColor: ['rgb(245, 198, 53, 0.8)'],
                        borderColor: ['#46705F'],
                        borderWidth: 1,
                        borderRadius: 6,
                        borderSkipped: false,
                        barPercentage: 0.8
                    }
                ];
            }

            const config = this.getChartConfig(type);

            if(this.chart !== null)
                this.chart.destroy();

            const ctx = document.getElementById('stats-chart');
            if(ctx !== undefined)
                this.chart = new Chart(ctx, config);
        },
        getChartConfig(type) {
            const config = {
                type: type,
                plugins: [ChartDataLabels],
                data: {
                    labels: this.headers,
                    datasets: this.chart_data
                },
                options: {
                    responsive: true,
                    title: {
                        display: false
                    },
                    scales: {
                        y: {
                            ticks: {
                                // Include a dollar sign in the ticks for revenue and spend
                                callback: (value) => {
                                    if(this.current_metric === 'revenue' || this.current_metric.indexOf("cost") > -1)
                                        return this.currency(value);
                                    else
                                        return this.number(value);
                                }
                            }
                        }
                    },
                    plugins: {
                        legend: {
                            position: 'bottom'
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) => {
                                    //Format the tooltip
                                    if(this.current_metric === 'revenue' || this.current_metric.indexOf("cost") > -1)
                                        return this.currency(context.parsed.y);
                                    else
                                        return this.number(context.parsed.y);
                                }
                            }
                        },
                        datalabels: {
                            anchor: (type === 'bar' ? 'end' : ""),
                            color: () => '#2A324B',
                            align: 'start',
                            formatter: (value) => {
                                if(this.current_metric === 'revenue' || this.current_metric.indexOf("cost") > -1)
                                    return "$" + this.num_abbr(value);
                                else
                                    return this.num_abbr(value);
                            }
                        }
                    }
                }
            };
            return config;
        },
        monthDiff(dateFrom, dateTo) {
            if(dateTo.getUTCDate() === dateFrom.getUTCDate())
                return dateTo.getUTCMonth() - dateFrom.getUTCMonth() +
                    (12 * (dateTo.getUTCFullYear() - dateFrom.getUTCFullYear()));
            else
                return dateTo.getUTCMonth() - dateFrom.getUTCMonth() +
                    (12 * (dateTo.getUTCFullYear() - dateFrom.getUTCFullYear())) + 1;
        },
        getUniqueArray(array) {
            return [...new Set(array)];
        },
        getDateAsString(value) {
            return moment(String(value)).utc().format('YYYY-MM-DD');
        }
    }
});
</script>
